import _ from 'lodash';
import React, {FC, useEffect, useMemo} from 'react';
import {connect} from 'react-redux';

import {AsyncTableSource} from '../../../../../../../../src/components/common/table/source/asyncTableSource';
import {convertDynamicColumnToColumn} from '../../../../../../../../src/components/common/table/tableColumn';
import {ResolveDispatchProps, useAppDispatch} from '../../../../../../../../src/helpers/reduxHelpers';
import {getCustomFieldsForAccountsFlat} from '../../../../../../../../src/store/data/customFields/customFieldsSelectors';
import {
  makeGetContactsManagerParameters,
  makeGetContactsManagerSearchState,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {
  contactsManagerSearchAccountsCloseSidePanelAndRemoveSelectedAction,
  contactsManagerSearchTablesForceReloadedAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/search/contactsManagerSearchActions';
import {openAccountDetailsAction} from '../../../../../../../../src/store/panels/contacts/contactsManager/sidePanel/contactsManagerSidePanelActions';
import {
  contactsManagerFetchSearchAccountsTableDataAsyncAction,
  contactsManagerSetUpAccountsTableAction,
  contactsManagerTableAccountIdsAddSelectedAction,
  contactsManagerTableAccountIdsRemoveSelectedAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {ContactsManagerTableStatusesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {compareName} from '../../../../../../../../srcCommon/helpers/localeHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../srcCommon/helpers/reselectHelpers';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {
  AccountsTableColumnsEnum,
  getDynamicColumnConfigFromCustomFieldType,
} from '../common/accounts/accountsTableConfig';
import {ContactsManagerCustomFieldColumnMetadata} from '../common/contactsManagerCustomFieldTypes';
import {AccountsSearchTable} from './accountsSearchTable';

/*
 * Props.
 */

interface AccountsTableContainerPublicProps {
  panelId: ContactsManagerPanelId;
  renderEmptyState: Renderer;
}

type AccountsTableContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type AccountsTableContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type AccountsTableContainerComponentProps = AccountsTableContainerPublicProps &
  AccountsTableContainerStateProps &
  AccountsTableContainerDispatchProps;

/*
 * Selectors.
 */

function makeGetDynamicColumns() {
  return createSelector([getCustomFieldsForAccountsFlat], (customFields) =>
    _([...customFields])
      .sort((customField1, customField2) => compareName(customField1.name, customField2.name))
      .map((customField) => {
        const columnConfig = getDynamicColumnConfigFromCustomFieldType(customField.type);
        if (!columnConfig) {
          return undefined;
        }

        return convertDynamicColumnToColumn<
          AccountsTableColumnsEnum,
          ContactsManagerCustomFieldColumnMetadata
        >(`${customField.id}`, customField.name, columnConfig, {
          customField,
        });
      })
      .compact()
      .value(),
  );
}

function makeGetQuery() {
  return createSelector([makeGetContactsManagerParameters()], (params) => {
    if (params.type !== ContactsManagerParametersTypesEnum.SEARCH) {
      return '';
    }
    return params.query;
  });
}

function makeGetAccountIds(
  getContactsManagerSearchState: ReturnType<typeof makeGetContactsManagerSearchState>,
) {
  return createSelector(
    [getContactsManagerSearchState],
    (searchState) => searchState?.accountsTableState?.accountIds ?? [],
  );
}

function makeGetSelectedAccountIds(
  getContactsManagerSearchState: ReturnType<typeof makeGetContactsManagerSearchState>,
) {
  return createSelector(
    [getContactsManagerSearchState],
    (searchState) => searchState?.accountsTableState?.selectedAccountIds ?? [],
  );
}

function makeGetPaginationData(
  getContactsManagerSearchState: ReturnType<typeof makeGetContactsManagerSearchState>,
) {
  return createSelector(
    [getContactsManagerSearchState],
    (searchState) => searchState?.accountsTableState?.paginationData,
  );
}

/*
 * Component.
 */

export const AccountsSearchTableContainerComponent: FC<AccountsTableContainerComponentProps> = (props) => {
  const {
    panelId,
    dynamicColumns,
    accountIds,
    paginationData,
    selectedAccountIds,
    query,
    searchState,
    onAddSelectedAccountIds,
    onRemoveSelectedAccountIds,
    renderEmptyState,
    onCloseSidePanelAndRemoveSelectedAccountIds,
  } = props;

  const dispatch = useAppDispatch();

  // When we change tabs, we should remove any selected account ids.
  useInitialEffect(() => () => onCloseSidePanelAndRemoveSelectedAccountIds(panelId));

  // If we don't have any selected account ids and the table is loading close the sidepanel
  useEffect(() => {
    if (
      selectedAccountIds.length === 0 &&
      searchState?.accountsTableState?.status === ContactsManagerTableStatusesEnum.LOADING
    ) {
      onCloseSidePanelAndRemoveSelectedAccountIds(panelId);
    }
  }, [
    onCloseSidePanelAndRemoveSelectedAccountIds,
    panelId,
    searchState?.accountsTableState?.status,
    selectedAccountIds,
  ]);

  useEffect(() => {
    if (selectedAccountIds.length === 1) {
      dispatch(openAccountDetailsAction());
    }
  }, [dispatch, selectedAccountIds]);

  useEffect(() => {
    if (!searchState?.accountsTableState) {
      dispatch(contactsManagerSetUpAccountsTableAction());
    }
  }, [dispatch, searchState?.accountsTableState]);

  const source = useMemo(
    () =>
      new AsyncTableSource({
        onReset: async () => dispatch(contactsManagerSearchTablesForceReloadedAction(panelId)),
        fetcher: async ({cancelToken}) =>
          dispatch(contactsManagerFetchSearchAccountsTableDataAsyncAction(panelId, query, cancelToken)),
      }),
    [panelId, query, dispatch],
  );

  return (
    <AccountsSearchTable
      panelId={panelId}
      dynamicColumns={dynamicColumns}
      accountIds={accountIds}
      selectedAccountIds={selectedAccountIds}
      paginationData={paginationData}
      source={source}
      onAddSelectedAccountIds={onAddSelectedAccountIds}
      onRemoveSelectedAccountIds={onRemoveSelectedAccountIds}
      renderEmptyState={renderEmptyState}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AccountsTableContainerPublicProps>()(() => {
  const getContactsManagerSearchState = makeGetContactsManagerSearchState();
  return {
    searchState: getContactsManagerSearchState,
    dynamicColumns: makeGetDynamicColumns(),
    accountIds: makeGetAccountIds(getContactsManagerSearchState),
    selectedAccountIds: makeGetSelectedAccountIds(getContactsManagerSearchState),
    paginationData: makeGetPaginationData(getContactsManagerSearchState),
    query: makeGetQuery(),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<AccountsTableContainerPublicProps>()(({panelId}) => ({
  onCloseSidePanelAndRemoveSelectedAccountIds:
    contactsManagerSearchAccountsCloseSidePanelAndRemoveSelectedAction,
  onAddSelectedAccountIds: (accountIds: ReadonlyArray<number>) =>
    contactsManagerTableAccountIdsAddSelectedAction(panelId, accountIds),
  onRemoveSelectedAccountIds: (accountIds: ReadonlyArray<number>) =>
    contactsManagerTableAccountIdsRemoveSelectedAction(panelId, accountIds),
}));

export const AccountsSearchTableContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(AccountsSearchTableContainerComponent);
